import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Link,
  LinearProgress,
  Button,
  Modal,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { apiClient } from "../../utils/apiClient";
import { InternalEmailThreadComponent } from "../../internal/Email/InternalEmailThreadComponent";
import {
  DataTableColumn,
  DataTableComponent,
} from "../../components/EmailDataTableComponent/DataTableComponent";
import { DemoQuote, DemoRate, quoteColumns } from "./rateColumnDefinition";
import DobbyDemoPage from "../DobbyDemoPage";
import { RatePriceFluctuationComponent } from "./RatePriceFluctuationComponent";
import { DemoRatesAllResponse } from "@services/demo/types";

const QUOTE_THREAD_ID = "demo-quote-thread-0";
const QUOTE_MESSAGE_ID = "demo-quote-message-0";

export const DemoQuotesPage = () => {
  const [rates, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const [scaleFactorWidthRatio, setScaleFactorWidthRatio] = useState(0.5); // Default width ratio
  const [searchFilter, setSearchFilter] = useState("");

  // const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    fieldName: string;
    direction: "asc" | "desc";
  }>({
    fieldName: "date",
    direction: "desc",
  });

  const filteredQuotes = useMemo(() => {
    const tokens = searchFilter.toLowerCase().split(/\s+/).filter(Boolean);

    return rates.filter((rate: DemoQuote) => {
      const fields = [
        rate.zip_code,
        rate.email,
        rate.city,
        rate.state,
        rate.pricing_person,
        rate.vendor_name,
      ]
        .map((f) => f?.toLowerCase() || "")
        .join(" "); // Combine for easier token matching

      return tokens.every((token) => fields.includes(token));
    });
  }, [rates, searchFilter]);

  const sortQuotes = (
    rates: any[],
    fieldName: string,
    direction: "asc" | "desc"
  ) => {
    return [...rates].sort((a, b) => {
      const aValue = a[fieldName];
      const bValue = b[fieldName];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response =
          await apiClient.post<DemoRatesAllResponse>(
            // use same endpoint
            "/demo/rates/all",
            {}
          );
        const fetchedRates = response.rates;
        const quotes: DemoQuote[] = fetchedRates.map((rate: any, i) => {
          rate.quote_id = `QT2312${i}`;
          rate.won_status = ["won", "lost"][
            Math.floor(Math.random() * 2)
          ] as any;
          if (rate.won_status === "lost" && Math.random() > 0.5) {
            rate.winning_price = Math.floor(rate.drayage - Math.random() * 50);
          }

          return rate;
        });

        setQuotes(
          sortQuotes(quotes, sortConfig.fieldName, sortConfig.direction)
        );
      } catch (err) {
        setError("Failed to fetch rates");
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  const handleSort = (column: DataTableColumn<any>) => {
    if (!column.fieldName) return;
    const newDirection =
      sortConfig.fieldName === column.fieldName &&
      sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({
      fieldName: column.fieldName as string,
      direction: newDirection,
    });

    setQuotes(sortQuotes(rates, column.fieldName as string, newDirection));
  };

  const handleRowClick = (row: DemoQuote) => {
    setSelectedThreadId(QUOTE_THREAD_ID);
    setSelectedMessageId(QUOTE_MESSAGE_ID);
    setSelectedRowId(row.id);
  };

  const closeThreadView = () => {
    setSelectedThreadId(null);
    setSelectedMessageId(null);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();

    const startX = event.clientX;
    const startFactor = scaleFactorWidthRatio;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      let newFactor = startFactor - deltaX / window.innerWidth;

      newFactor = Math.min(Math.max(newFactor, 0.2), 0.8); // Limit scale factor
      setScaleFactorWidthRatio(newFactor);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const columns = quoteColumns();

  return (
    <DobbyDemoPage fullHeight title=" Quotes">
      <Box
        display="flex"
        flexDirection="row"
        height="100%"
        p={2}
        style={{ position: "relative" }}
      >
        <Box style={{ flex: selectedThreadId ? 1 - scaleFactorWidthRatio : 1 }}>
          {loading && (
            <LinearProgress
              style={{ position: "absolute", top: 0, left: 0, width: "100%" }}
            />
          )}
          {error && <Typography color="error">{error}</Typography>}

          {/* Search filters */}
          <Box
            display="flex"
            gap={2}
            mb={2}
            alignContent={"middle"}
            alignItems={"center"}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              sx={{ width: "400px" }}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <Typography variant="caption" color="textSecondary">
              Showing {filteredQuotes.length - 68} Quotes
              {searchFilter && (
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => setSearchFilter("")}
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Clear search
                </Link>
              )}
            </Typography>
          </Box>

          <DataTableComponent
            rows={filteredQuotes}
            columns={columns}
            showColumnVisibilityToggle
            keyField="id"
            sortColumn={sortConfig.fieldName as any}
            sortDirection={sortConfig.direction}
            selectedRowKey={selectedRowId || undefined}
            onSort={handleSort}
            onRowClick={handleRowClick}
            sx={{ maxHeight: "calc(100% - 50px)" }}
            stickyHeader
          />
        </Box>

        {selectedThreadId && (
          <>
            <Box
              style={{ flex: scaleFactorWidthRatio }}
              sx={{
                position: "relative",
                height: "100%",
              }}
            >
              <SeparatorBar
                onMouseDown={handleMouseDown}
                closeThreadView={closeThreadView}
              />
              <Box
                sx={{
                  height: "100%",
                  overflow: "auto",
                }}
              >
                <Box
                  style={{
                    position: "absolute",
                    // top: (1 / scaleFactorWidthRatio) * 5,
                    top: 0,
                    transformOrigin: "top left",
                    width: `${100 / (scaleFactorWidthRatio * 2)}%`,
                    height: `${100 / (scaleFactorWidthRatio * 2)}%`,
                    transform: `scale(${scaleFactorWidthRatio * 2})`,
                    overflow: "hidden",
                  }}
                >
                  <InternalEmailThreadComponent
                    threadId={selectedThreadId}
                    loadOnlySelectedMessage
                    selectedMessageId={selectedMessageId || undefined}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </DobbyDemoPage>
  );
};

const SeparatorBar: React.FC<{
  onMouseDown: React.MouseEventHandler;
  closeThreadView: React.MouseEventHandler;
}> = ({ onMouseDown, closeThreadView }) => (
  <>
    <Box
      sx={{
        position: "absolute",
        width: "4px",
        height: "100%",
        cursor: "col-resize",
        backgroundColor: "#eee",
        borderRight: "1px solid #666",
        borderLeft: "1px solid #666",
        "&:hover": { backgroundColor: "#999" },
        zIndex: 9999,
      }}
      onMouseDown={onMouseDown}
    />
    <Button
      sx={{
        position: "absolute",
        color: "#333",
        backgroundColor: "#eee",
        border: "1px solid",
        borderColor: "#666",
        borderLeft: "1px solid",
        borderLeftColor: "#eee",
        top: "50%",
        left: 5,
        borderRadius: 0,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        p: 0.3,
        minWidth: 0,
        height: 40,
        fontSize: 10,
        zIndex: 9999,
      }}
      variant="outlined"
      onClick={closeThreadView}
    >
      {/* Close */}
      <ChevronRightIcon sx={{ fontSize: 18 }} />
    </Button>
  </>
);
